import clsx from 'clsx'
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts'
import { CHART_VIZ_TRACK, chartSemanticProgressColor } from '@/config/chartTheme'
import type { Figure } from '@/types/dashboards'
import FigureValue, { NO_VALUE, formatFigure } from './FigureValue'

interface ScoreDialProps {
  /** 0–100 overall score. A null value draws the empty track and reads '--'. */
  figure: Figure
  size?: number
  className?: string
}

/** The large overall ring above a scorecard. */
export default function ScoreDial({ figure, size = 148, className }: ScoreDialProps) {
  const numeric = typeof figure.value === 'number' ? figure.value : null
  const score = numeric == null ? null : Math.max(0, Math.min(100, numeric))
  const isBlank = formatFigure(figure) === NO_VALUE

  return (
    <div className={clsx('relative flex-shrink-0', className)} style={{ height: size }}>
      <ResponsiveContainer width="100%" height={size}>
        <RadialBarChart
          innerRadius="74%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          data={[{ name: figure.label, value: score ?? 0 }]}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar
            dataKey="value"
            cornerRadius={999}
            background={{ fill: CHART_VIZ_TRACK }}
            fill={chartSemanticProgressColor(score ?? 0)}
            isAnimationActive={false}
          />
        </RadialBarChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <div
          className={clsx(
            'pointer-events-auto text-[26px] font-semibold leading-none',
            isBlank ? 'text-stone-600' : 'text-stone-100',
          )}
        >
          <FigureValue figure={figure} />
        </div>
        <div className="terminal-kicker mt-1.5 !text-[8px]">{figure.label}</div>
      </div>
    </div>
  )
}
